import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useGorgiasIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-gorgias-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/ui/components/input-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";
const formSchema = z.object({
  activated: z.boolean(),
  email: z.string().email(),
  apiKey: z.string(),
  domainUrl: z.string().optional(),
});

// The backend builds `https://{domainUrl}.gorgias.com/api/tickets`, so only the
// bare subdomain must be stored. Merchants often paste the full URL: strip the
// protocol, the .gorgias.com suffix and any path.
function sanitizeSubDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/\.gorgias\.com\/?.*$/i, "")
    .replace(/\/.*$/, "");
}

const GorgiasIntegration = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { activated, email, apiKey, domainUrl } = shop.integrations.gorgias;

  // Values saved before this field was sanitized may hold a full URL. Display
  // the normalized subdomain and keep Save enabled so one click persists it.
  const sanitizedDomainUrl = sanitizeSubDomain(domainUrl ?? "");
  const needsNormalization = (domainUrl ?? "") !== sanitizedDomainUrl;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, email, apiKey, domainUrl: sanitizedDomainUrl },
  });

  const { mutateAsync: updateGorgiasIntegration } =
    useGorgiasIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateGorgiasIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.email")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.gorgiasToken")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="domainUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.domainUrl")}
              </FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupInput
                    {...field}
                    onChange={(e) =>
                      field.onChange(sanitizeSubDomain(e.target.value))
                    }
                    placeholder="mysub"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>.gorgias.com</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={!form.formState.isDirty && !needsNormalization}
        >
          {t("misc.save")}
        </Button>
      </form>
    </Form>
  );
};

export function GorgiasIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useGorgiasIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.gorgias) return null;

  return (
    <IntegrationPreview
      title="Gorgias"
      bgColor="#FF9780"
      textColor="black"
      logo="/integrations/gorgias.png"
      description={t("settings.integrations.thirdpart.gorgias.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.gorgias.activated}
      seeMoreUrl="https://gorgias.com"
    >
      <GorgiasIntegration />
    </IntegrationPreview>
  );
}
